import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { tableShapeToIcon } from "../../../models/base/TableShapeType";
import { LiveOrderTableDTO } from "../../../models/table/table.response";
import { TableStatusType, tableStatusTypeToColor, tableStatusTypeToString } from "../../../models/base/TableStatusType";

const GRID = 1000;
const MIN_SIZE_PCT = 50 / GRID;

interface LiveOrdersTablesProps {
    tables: LiveOrderTableDTO[];
    onTablesChange?: (tables: LiveOrderTableDTO[]) => void;
    disablePositioning: boolean;
    onSelectedTableChange?: (tableId: string) => void;
    backgroundImageUrl?: string;
}
const LiveOrdersTables: React.FC<LiveOrdersTablesProps> = ({ tables, onTablesChange, disablePositioning, onSelectedTableChange, backgroundImageUrl }) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerRect, setContainerRect] = useState<DOMRect | null>(null);

    const [selectedId, setSelectedId] = useState<string | null>(null);

    useLayoutEffect(() => {
        if (!containerRef.current)
            return;

        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setContainerRect(rect);
        }
    }, []);


    const updateTable = (id: string, patch: Partial<LiveOrderTableDTO>) => {
        if (!onTablesChange)
            return;
        const updated = tables.map((t) => (t.id === id ? { ...t, ...patch } : t));
        onTablesChange(updated);
    };


    function clamp(v: number, min: number, max: number) {
        return Math.max(min, Math.min(max, v));
    }

    function snapPct(v: number) {
        const step = 1 / GRID;
        return Math.round(v / step) * step;
    }

    return (
        <div
            ref={containerRef}
            className="admin-settings-box-div lo-tables-div"
            // bitno: da Rnd (position:absolute) radi u ovom container-u
            style={{ position: "relative", backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : "url(https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/696a5e623e2e05de670beb86_Restaurant-3D-Floor-Plan.jpg)", backgroundSize: "cover" }}
            onMouseDown={() => setSelectedId(null)}
        >
            {containerRect &&
                tables.map((t) => {

                    const xPx = t.xCordinatePercentage * containerRect.width / 100;
                    const yPx = t.yCordinatePercentage * containerRect.height / 100;
                    const wPx = t.widthPercentage * containerRect.width / 100;
                    const hPx = t.heightPercentage * containerRect.height / 100;

                    const stClass = tableStatusTypeToColor(t.currentStatus);
                    const isSelected = selectedId === t.id;

                    return (
                        <Rnd
                            key={t.id}
                            bounds="parent"
                            position={{ x: xPx, y: yPx }}
                            size={{ width: wPx, height: hPx }}
                            // Snap na grid u px (računamo iz container dimenzije)
                            dragGrid={
                                containerRect
                                    ? [Math.max(1, containerRect.width / GRID), Math.max(1, containerRect.height / GRID)]
                                    : [10, 10]
                            }
                            resizeGrid={
                                containerRect
                                    ? [Math.max(1, containerRect.width / GRID), Math.max(1, containerRect.height / GRID)]
                                    : [10, 10]
                            }
                            minWidth={containerRect ? MIN_SIZE_PCT * containerRect.width : 40}
                            minHeight={containerRect ? MIN_SIZE_PCT * containerRect.height : 40}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                                setSelectedId(t.id);
                                if (onSelectedTableChange) {
                                    onSelectedTableChange(t.id);
                                }
                            }}
                            onDragStop={(e, d) => {
                                if (disablePositioning)
                                    return;

                                const r = containerRect;

                                if (!r)
                                    return;

                                // px -> pct
                                let xPct = d.x / r.width * 100;
                                let yPct = d.y / r.height * 100;

                                // snap + clamp (da ne izađe van)
                                xPct = snapPct(xPct);
                                yPct = snapPct(yPct);
                                xPct = clamp(xPct, 0, 100 - t.widthPercentage);
                                yPct = clamp(yPct, 0, 100 - t.heightPercentage);

                                updateTable(t.id, { xCordinatePercentage: xPct, yCordinatePercentage: yPct });

                                // OVDE kasnije šalješ PATCH ka backendu (debounce)
                                // saveTableLayout(t.id, { xPct, yPct, wPct: t.wPct, hPct: t.hPct });
                            }}
                            onResizeStop={(e, dir, ref, delta, pos) => {
                                if (disablePositioning)
                                    return;

                                const r = containerRect;
                                if (!r) return;

                                // nove dimenzije px
                                const newWPx = ref.offsetWidth;
                                const newHPx = ref.offsetHeight;

                                // px -> pct
                                let wPct = newWPx / r.width * 100;
                                let hPct = newHPx / r.height * 100;

                                let xPct = pos.x / r.width * 100;
                                let yPct = pos.y / r.height * 100;
                                // snap + min + clamp
                                wPct = Math.max(MIN_SIZE_PCT, snapPct(wPct));
                                hPct = Math.max(MIN_SIZE_PCT, snapPct(hPct));
                                xPct = snapPct(xPct);
                                yPct = snapPct(yPct);

                                xPct = clamp(xPct, 0, 100 - wPct);
                                yPct = clamp(yPct, 0, 100 - hPct);

                                updateTable(t.id, { xCordinatePercentage: xPct, yCordinatePercentage: yPct, widthPercentage: wPct, heightPercentage: hPct });

                                // OVDE kasnije šalješ PATCH ka backendu (debounce)
                                // saveTableLayout(t.id, { xPct, yPct, wPct, hPct });
                            }}
                        >
                            <div
                                className={[
                                    "lo-table-div",
                                    stClass,
                                    isSelected ? "selected" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    position: "relative",
                                }}
                            >
                                <div className={["lo-table-shape-image-div", stClass].filter(Boolean).join(" ")}>
                                    <img
                                        src={tableShapeToIcon(t.shape)}
                                        alt=""
                                        className="lo-table-shape-image"
                                        draggable={false}
                                    />
                                </div>

                                <div className="lo-table-number">Sto {t.number}</div>

                                <div className="small-box-div small">
                                    <div>{tableStatusTypeToString(t.currentStatus)}</div>
                                </div>

                                {t.callWaiter && (
                                    <div className="lo-table-hand-up-image-div">
                                        <img
                                            loading="lazy"
                                            src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1ecd45c56aefc25619d_palm.png"
                                            alt=""
                                            className="icon-size-32"
                                            draggable={false}
                                        />
                                    </div>
                                )}
                            </div>
                        </Rnd>
                    );
                })}
        </div>
    );
};

export default LiveOrdersTables;
